import React, { useState, useEffect, useCallback } from "react";
import { authFetch, API_BASE } from "../api";
import ViewToggle from "./shared/ViewToggle";
import SearchBar from "./shared/SearchBar";
import BulkActionBar from "./shared/BulkActionBar";
import "./shared/shared.css";
import "./HistoryPage.css";

// Conversations are id-keyed only (no legacy name alias) — the backend
// returns { appId, appName, messages, updatedAt } per conversation. appId is
// the app's real apps.id and is what every fetch/delete below addresses;
// appName is display-only.
export default function HistoryPage({ onLoadConversation }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem("forge-history-view") || "card");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());

  const fetchConversations = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/chat/conversations`);
      const data = await res.json();
      setConversations(
        (data.conversations || [])
          .filter((c) => c.appId && c.appName)
          .sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""))
      );
    } catch (err) {
      console.error("Failed to fetch conversations:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  const filtered = conversations.filter((c) =>
    !search || c.appName.toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelect = (appId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(appId) ? next.delete(appId) : next.add(appId);
      return next;
    });
  };

  const deleteConversation = async (appId) => {
    try {
      await authFetch(`${API_BASE}/chat/conversations/${encodeURIComponent(appId)}`, { method: "DELETE" });
      setConversations((prev) => prev.filter((c) => c.appId !== appId));
      setSelected((prev) => { const next = new Set(prev); next.delete(appId); return next; });
    } catch (err) {
      console.error("Failed to delete conversation:", err);
    }
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} conversations? This cannot be undone.`)) return;
    for (const appId of selected) {
      await deleteConversation(appId);
    }
    setSelected(new Set());
  };

  const loadConversation = async (appId) => {
    try {
      const res = await authFetch(`${API_BASE}/chat/conversations/${encodeURIComponent(appId)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.error === 'string' ? data.error : data.error?.message || "Request failed");
      if (onLoadConversation) onLoadConversation(data);
    } catch (err) {
      console.error("Failed to load conversation:", err);
    }
  };

  return (
    <div className="history-page">
      <div className="page-header">
        <h2>Conversation History</h2>
        <div className="page-header-right">
          <span className="apps-stat">{conversations.length} conversations</span>
        </div>
      </div>

      <div className="page-toolbar">
        <div className="page-toolbar-left">
          <SearchBar value={search} onChange={setSearch} placeholder="Search conversations..." />
        </div>
        <div className="page-toolbar-right">
          <button className="history-refresh-btn" onClick={fetchConversations} title="Refresh">&#x21bb;</button>
          <ViewToggle view={view} onChange={setView} storageKey="forge-history-view" />
        </div>
      </div>

      <BulkActionBar
        count={selected.size}
        total={filtered.length}
        onSelectAll={() => setSelected(new Set(filtered.map((c) => c.appId)))}
        onDeselectAll={() => setSelected(new Set())}
      >
        <button className="bulk-btn-danger" onClick={bulkDelete}>Delete Selected</button>
      </BulkActionBar>

      <div className="page-body">
        {loading ? (
          <div className="page-empty">Loading conversations...</div>
        ) : filtered.length === 0 ? (
          <div className="page-empty">
            {search ? `No conversations matching "${search}"` : "No conversation history yet."}
          </div>
        ) : view === "table" ? (
          <table className="data-table">
            <thead>
              <tr>
                <th className="td-checkbox">
                  <input
                    type="checkbox"
                    checked={selected.size === filtered.length && filtered.length > 0}
                    onChange={(e) => setSelected(e.target.checked ? new Set(filtered.map((c) => c.appId)) : new Set())}
                  />
                </th>
                <th>Name</th>
                <th>Messages</th>
                <th>Last Edited</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((conv) => (
                <tr key={conv.appId}>
                  <td className="td-checkbox">
                    <input type="checkbox" checked={selected.has(conv.appId)} onChange={() => toggleSelect(conv.appId)} />
                  </td>
                  <td data-label="Name">{conv.appName}</td>
                  <td data-label="Messages">{conv.messageCount}</td>
                  <td data-label="Last Edited" style={{ color: "var(--color-text-faint)", fontSize: 12 }}>
                    {conv.updatedAt ? new Date(conv.updatedAt).toLocaleDateString() : "—"}
                  </td>
                  <td data-label="Actions">
                    <div className="td-actions">
                      <button className="apps-btn apps-btn-edit" onClick={() => loadConversation(conv.appId)}>Resume</button>
                      <button className="apps-btn apps-btn-danger" onClick={() => { if (window.confirm(`Delete "${conv.appName}" history?`)) deleteConversation(conv.appId); }}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="history-grid">
            {filtered.map((conv) => (
              <div key={conv.appId} className="history-card">
                <input type="checkbox" checked={selected.has(conv.appId)} onChange={() => toggleSelect(conv.appId)} style={{ flexShrink: 0 }} />
                <div className="history-card-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4H16V14H6L4 16V4Z" />
                  </svg>
                </div>
                <div className="history-card-info" onClick={() => loadConversation(conv.appId)} style={{ cursor: "pointer", flex: 1 }}>
                  <span className="history-card-name">{conv.appName}</span>
                  <span className="history-card-meta">
                    {conv.messageCount} messages
                    {conv.updatedAt && ` · ${new Date(conv.updatedAt).toLocaleDateString()}`}
                  </span>
                </div>
                <button
                  className="history-delete-btn"
                  onClick={() => { if (window.confirm(`Delete "${conv.appName}" history?`)) deleteConversation(conv.appId); }}
                  title="Delete"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
